import { ChevronDownIcon, CopyIcon, MailIcon } from "lucide-react";

import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const EmailDropdown = ({
  email,
  isReadOnly = false,
  onAddEmail,
  onSendEmail,
}: {
  email?: string;
  // Viewing surfaces (the application review portal) show the address as
  // plain text with no actions behind it.
  isReadOnly?: boolean;
  onAddEmail?: () => void;
  onSendEmail?: () => void;
}) => {
  /* Handlers */
  const handleCopyEmail = () => {
    if (!email) return;

    void navigator.clipboard.writeText(email);
  };

  /* Render */
  const renderMissingEmail = () => {
    if (!onAddEmail) return null;

    return (
      <Button
        className="-m-2 h-auto gap-2 rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950"
        onClick={onAddEmail}
        type="button"
        variant="ghost"
      >
        <MailIcon
          className="h-4 w-4 shrink-0 text-slate-400"
          strokeWidth={1.5}
        />
        <Text className="text-xs font-normal text-slate-500">—</Text>
      </Button>
    );
  };

  const render = () => {
    if (!email) return isReadOnly ? null : renderMissingEmail();
    if (isReadOnly) {
      return (
        <Row className="items-center gap-2">
          <MailIcon
            className="h-4 w-4 shrink-0 text-slate-400"
            strokeWidth={1.5}
          />
          <Text className="text-xs font-normal text-slate-500">{email}</Text>
        </Row>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="group/email -m-2 h-auto gap-2 rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950 data-[state=open]:bg-slate-100 data-[state=open]:text-slate-950"
            type="button"
            variant="ghost"
          >
            <MailIcon
              className="h-4 w-4 shrink-0 text-slate-400 group-hover/email:hidden group-data-[state=open]/email:hidden"
              strokeWidth={1.5}
            />
            <ChevronDownIcon
              className="hidden h-4 w-4 shrink-0 text-slate-400 group-hover/email:block group-hover/email:text-slate-950 group-data-[state=open]/email:block group-data-[state=open]/email:text-slate-950"
              strokeWidth={1.5}
            />
            <Text className="text-xs font-normal text-slate-500 group-hover/email:text-slate-950 group-data-[state=open]/email:text-slate-950">
              {email}
            </Text>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuItem onClick={onSendEmail}>
            <MailIcon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
            Send email
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleCopyEmail}>
            <CopyIcon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
            Copy email address
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return render();
};
